import { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { aiAuthFetch } from "./aiAuthFetch";
import "./fabric_purchase.css";

const COL = "fabric_purchases";
const COL_ISSUES = "fabric_issues";
const SETTINGS_API = "https://maviinci-ai-bluepearl-e9750.web.app/api/settings";

const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const inr = (v) => "₹" + Math.round(num(v)).toLocaleString("en-IN");
const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};
const fmtTime = (ts) => {
  if (!ts) return "";
  return new Date(ts).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
};
const stockKey = (p) => `${(p.brand || "").toLowerCase()}|${(p.article || "").toLowerCase()}|${(p.shade || "").toLowerCase()}`;

/**
 * Fabric Purchase & Stock — shared for Website + Campus.
 * Stock available = Σ purchases − Σ fabric_issues (app Store issue flow).
 */
function Fabric_purchase() {
  const [purchases, setPurchases] = useState([]);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterBiz, setFilterBiz] = useState("all"); // all | website | campus
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("stock"); // stock | log | issues | add

  // Catalog (same as app / MUSTAQBIL)
  const [brands, setBrands] = useState([]);
  const [articles, setArticles] = useState([]);
  const [shades, setShades] = useState([]);

  const [form, setForm] = useState({
    biz: "website", brand: "", article: "", shade: "", mtr: "", cost: "", note: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const unsub = db.collection(COL).orderBy("ts", "desc").limit(500).onSnapshot(
      (snap) => {
        setPurchases(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => { console.error("fabric_purchases", err); setLoading(false); }
    );
    const unsubI = db.collection(COL_ISSUES).orderBy("ts", "desc").limit(500).onSnapshot(
      (snap) => setIssues(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        console.error("fabric_issues orderBy failed, falling back", err);
        // Index / empty-collection edge: still load without order
        db.collection(COL_ISSUES).limit(500).get().then((snap) => {
          const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          rows.sort((a, b) => (b.ts || 0) - (a.ts || 0));
          setIssues(rows);
        }).catch((e2) => console.error("fabric_issues", e2));
      }
    );
    return () => { unsub(); unsubI(); };
  }, []);

  useEffect(() => {
    aiAuthFetch(SETTINGS_API).then((r) => r.json()).then((s) => {
      setBrands(Array.isArray(s.brands) ? s.brands : []);
      const suit = Array.isArray(s.suiting_fabrics) ? s.suiting_fabrics : [];
      const shirt = Array.isArray(s.shirting_fabrics) ? s.shirting_fabrics : [];
      setArticles([...suit, ...shirt]);
      setShades(Array.isArray(s.shades) ? s.shades : []);
    }).catch(() => {});
  }, []);

  const articleOptions = useMemo(() => {
    const f = form.brand
      ? articles.filter((x) => (x.brand || "") === form.brand)
      : articles;
    return Array.from(new Set(f.map((x) => x.name).filter(Boolean))).sort();
  }, [articles, form.brand]);

  const todayStart = useMemo(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime();
  }, []);

  const totals = useMemo(() => {
    let purchased = 0, cost = 0, today = 0, web = 0, campus = 0, issued = 0;
    purchases.forEach((p) => {
      const m = num(p.mtr);
      purchased += m;
      cost += num(p.cost);
      if ((p.ts || 0) >= todayStart) today += m;
      if ((p.biz || "website") === "campus") campus += m; else web += m;
    });
    issues.forEach((i) => { issued += num(i.mtr); });
    const available = Math.max(0, purchased - issued);
    return {
      purchased, available, issued, cost, today, web, campus,
      count: purchases.length, issueCount: issues.length,
    };
  }, [purchases, issues, todayStart]);

  // Stock by brand + article + shade = Σ purchases − Σ fabric_issues
  const stockRows = useMemo(() => {
    const map = new Map();
    purchases.forEach((p) => {
      if (filterBiz !== "all" && (p.biz || "website") !== filterBiz) return;
      const key = stockKey(p);
      const cur = map.get(key) || {
        brand: p.brand || "", article: p.article || "", shade: p.shade || "",
        purchased: 0, issued: 0, cost: 0, web: 0, campus: 0, entries: 0,
      };
      const m = num(p.mtr);
      cur.purchased += m;
      cur.cost += num(p.cost);
      cur.entries += 1;
      if ((p.biz || "website") === "campus") cur.campus += m; else cur.web += m;
      map.set(key, cur);
    });
    issues.forEach((i) => {
      if (filterBiz !== "all" && (i.biz || "website") !== filterBiz) return;
      const key = stockKey(i);
      const cur = map.get(key) || {
        brand: i.brand || "", article: i.article || "", shade: i.shade || "",
        purchased: 0, issued: 0, cost: 0, web: 0, campus: 0, entries: 0,
      };
      cur.issued += num(i.mtr);
      if (!cur.brand && i.brand) cur.brand = i.brand;
      if (!cur.article && i.article) cur.article = i.article;
      if (!cur.shade && i.shade) cur.shade = i.shade;
      map.set(key, cur);
    });
    let rows = Array.from(map.values()).map((r) => ({
      ...r,
      mtr: Math.max(0, r.purchased - r.issued),
    })).sort((a, b) => b.mtr - a.mtr);
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter((r) =>
      [r.brand, r.article, r.shade].join(" ").toLowerCase().includes(q)
    );
    return rows;
  }, [purchases, issues, filterBiz, search]);

  const logRows = useMemo(() => {
    let rows = purchases;
    if (filterBiz !== "all") rows = rows.filter((p) => (p.biz || "website") === filterBiz);
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter((p) =>
      [p.brand, p.article, p.shade, p.by, p.note, p.biz].join(" ").toLowerCase().includes(q)
    );
    return rows;
  }, [purchases, filterBiz, search]);

  const issueRows = useMemo(() => {
    let rows = issues;
    if (filterBiz !== "all") rows = rows.filter((p) => (p.biz || "website") === filterBiz);
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter((p) =>
      [p.brand, p.article, p.shade, p.by, p.orderId, p.garment, p.biz].join(" ").toLowerCase().includes(q)
    );
    return rows;
  }, [issues, filterBiz, search]);

  const addPurchase = async () => {
    if (!form.article.trim()) { alert("Article is required."); return; }
    if (!num(form.mtr)) { alert("Meters (MTR) is required."); return; }
    setSaving(true);
    try {
      await db.collection(COL).add({
        biz: form.biz === "campus" ? "campus" : "website",
        brand: form.brand.trim(),
        article: form.article.trim(),
        shade: form.shade.trim(),
        mtr: num(form.mtr),
        cost: num(form.cost),
        note: form.note.trim(),
        by: "Admin panel",
        date: new Date().toISOString(),
        ts: Date.now(),
      });
      setForm({ biz: form.biz, brand: "", article: "", shade: "", mtr: "", cost: "", note: "" });
      setTab("log");
    } catch (e) {
      console.error(e);
      alert("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const removePurchase = async (it) => {
    if (!window.confirm(`Delete purchase: ${it.article} · ${num(it.mtr)} m?`)) return;
    try { await db.collection(COL).doc(it.id).delete(); }
    catch (e) { console.error(e); alert("Could not delete."); }
  };

  return (
    <div className="fp-page">
      {/* Stats */}
      <div className="fp-stat-row">
        <div className="fp-stat">
          <span className="fp-stat-label">In hand</span>
          <span className="fp-stat-value">{totals.available.toFixed(1)} m</span>
        </div>
        <div className="fp-stat">
          <span className="fp-stat-label">Purchased</span>
          <span className="fp-stat-value">{totals.purchased.toFixed(1)} m</span>
        </div>
        <div className="fp-stat">
          <span className="fp-stat-label">Issued</span>
          <span className="fp-stat-value">{totals.issued.toFixed(1)} m</span>
        </div>
        <div className="fp-stat">
          <span className="fp-stat-label">Today buy</span>
          <span className="fp-stat-value">{totals.today.toFixed(1)} m</span>
        </div>
        <div className="fp-stat">
          <span className="fp-stat-label">Spend</span>
          <span className="fp-stat-value">{inr(totals.cost)}</span>
        </div>
      </div>

      {/* Tabs + filters */}
      <div className="fp-toolbar">
        <div className="fp-tabs">
          {[
            { id: "stock", label: "Stock" },
            { id: "log", label: `Purchase log (${totals.count})` },
            { id: "issues", label: `Issues (${totals.issueCount})` },
            { id: "add", label: "+ Add purchase" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              className={`fp-tab ${tab === t.id ? "is-active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="fp-filters">
          <div className="fp-biz-chips">
            {["all", "website", "campus"].map((b) => (
              <button
                key={b}
                type="button"
                className={`fp-chip ${filterBiz === b ? "is-active" : ""}`}
                onClick={() => setFilterBiz(b)}
              >
                {b === "all" ? "All" : b === "website" ? "Website" : "Campus"}
              </button>
            ))}
          </div>
          {tab !== "add" && (
            <input
              className="fp-search"
              placeholder="Search brand / article / shade…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}
        </div>
      </div>

      {loading ? (
        <div className="fp-empty">Loading purchases…</div>
      ) : tab === "add" ? (
        <div className="fp-form-card">
          <div className="fp-form-title">Add fabric purchase</div>
          <p className="fp-note">Saves to the same list the mobile app uses — both stay in sync.</p>
          <div className="fp-form-grid">
            <label>
              Business
              <select value={form.biz} onChange={(e) => setForm({ ...form, biz: e.target.value })}>
                <option value="website">Website</option>
                <option value="campus">Campus</option>
              </select>
            </label>
            <label>
              Brand
              <input
                list="fp-brands"
                value={form.brand}
                onChange={(e) => setForm({ ...form, brand: e.target.value, article: "" })}
                placeholder="Brand"
              />
              <datalist id="fp-brands">{brands.map((b) => <option key={b} value={b} />)}</datalist>
            </label>
            <label>
              Article
              <input
                list="fp-articles"
                value={form.article}
                onChange={(e) => setForm({ ...form, article: e.target.value })}
                placeholder="Article name"
              />
              <datalist id="fp-articles">{articleOptions.map((a) => <option key={a} value={a} />)}</datalist>
            </label>
            <label>
              Shade
              <input
                list="fp-shades"
                value={form.shade}
                onChange={(e) => setForm({ ...form, shade: e.target.value })}
                placeholder="Shade no."
              />
              <datalist id="fp-shades">{shades.map((s) => <option key={s} value={s} />)}</datalist>
            </label>
            <label>
              MTR
              <input type="number" min="0" step="0.1" value={form.mtr} onChange={(e) => setForm({ ...form, mtr: e.target.value })} placeholder="0" />
            </label>
            <label>
              Cost (₹)
              <input type="number" min="0" step="1" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} placeholder="0" />
            </label>
            <label className="fp-form-wide">
              Note
              <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Supplier / bill no." />
            </label>
          </div>
          <button type="button" className="fp-save" onClick={addPurchase} disabled={saving}>
            {saving ? "Saving…" : "Save purchase"}
          </button>
        </div>
      ) : tab === "stock" ? (
        stockRows.length === 0 ? (
          <div className="fp-empty">No stock yet. Add a purchase from the app or the form above.</div>
        ) : (
          <div className="fp-table-wrap">
            <table className="fp-table">
              <thead>
                <tr>
                  <th>Brand</th>
                  <th>Article</th>
                  <th>Shade</th>
                  <th className="r">In hand (m)</th>
                  <th className="r">Purchased</th>
                  <th className="r">Issued</th>
                  <th className="r">Spend</th>
                  <th className="c">Entries</th>
                </tr>
              </thead>
              <tbody>
                {stockRows.map((r, i) => (
                  <tr key={i} className={r.mtr <= 0 && r.issued > 0 ? "fp-row-oos" : ""}>
                    <td>{r.brand || "—"}</td>
                    <td><strong>{r.article || "—"}</strong></td>
                    <td>{r.shade || "—"}</td>
                    <td className="r mono">{r.mtr.toFixed(1)}</td>
                    <td className="r mono muted">{r.purchased.toFixed(1)}</td>
                    <td className="r mono muted">{r.issued ? r.issued.toFixed(1) : "—"}</td>
                    <td className="r mono">{r.cost ? inr(r.cost) : "—"}</td>
                    <td className="c mono muted">{r.entries}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="fp-note">In hand = purchased − issued (Store app MTR confirm writes fabric_issues).</p>
          </div>
        )
      ) : tab === "issues" ? (
        issueRows.length === 0 ? (
          <div className="fp-empty">No fabric issues yet. Store confirm on AGGREGATOR_ACCEPTED orders will appear here.</div>
        ) : (
          <div className="fp-table-wrap">
            <table className="fp-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Order</th>
                  <th>Garment</th>
                  <th>Brand</th>
                  <th>Article</th>
                  <th>Shade</th>
                  <th className="r">MTR</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {issueRows.map((p) => (
                  <tr key={p.id}>
                    <td className="mono">
                      {fmtDate(p.date)}
                      <span className="fp-time">{fmtTime(p.ts)}</span>
                    </td>
                    <td className="mono"><strong>{p.orderId || "—"}</strong></td>
                    <td>{p.garment || "—"}</td>
                    <td>{p.brand || "—"}</td>
                    <td><strong>{p.article || "—"}</strong></td>
                    <td>{p.shade || "—"}</td>
                    <td className="r mono">{num(p.mtr).toFixed(1)}</td>
                    <td className="muted">{p.by || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        logRows.length === 0 ? (
          <div className="fp-empty">No purchase entries match this filter.</div>
        ) : (
          <div className="fp-table-wrap">
            <table className="fp-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Biz</th>
                  <th>Brand</th>
                  <th>Article</th>
                  <th>Shade</th>
                  <th className="r">MTR</th>
                  <th className="r">Cost</th>
                  <th>By</th>
                  <th>Note</th>
                  <th className="c"></th>
                </tr>
              </thead>
              <tbody>
                {logRows.map((p) => (
                  <tr key={p.id}>
                    <td className="mono">
                      {fmtDate(p.date)}
                      <span className="fp-time">{fmtTime(p.ts)}</span>
                    </td>
                    <td>
                      <span className={`fp-biz-pill fp-biz-${p.biz === "campus" ? "campus" : "website"}`}>
                        {(p.biz || "website").toUpperCase()}
                      </span>
                    </td>
                    <td>{p.brand || "—"}</td>
                    <td><strong>{p.article || "—"}</strong></td>
                    <td>{p.shade || "—"}</td>
                    <td className="r mono">{num(p.mtr).toFixed(1)}</td>
                    <td className="r mono">{num(p.cost) ? inr(p.cost) : "—"}</td>
                    <td className="muted">{p.by || "—"}</td>
                    <td className="muted">{p.note || ""}</td>
                    <td className="c">
                      <button type="button" className="fp-del" onClick={() => removePurchase(p)} title="Delete">✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

export default Fabric_purchase;
